<script setup lang="ts">
import zod from "zod";

const route = useValidatedRoute({
  params: zod.object({ id: zod.coerce.number() }),
});
const { t } = useI18n();

const [{ data: task, error }, { data: categories }, { data: similar }] = await Promise.all([
  useAsyncData('task', () => TaskAPI.find({ params: { id: route.params.id } })),
  useAsyncData('categories', () => TaskAPI.categories()),
  useAsyncData('similar', () => TaskAPI.similar({ params: { taskId: route.params.id } })),
]);

if (error.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' });
}

const react = (reaction: 'like' | 'dislike') => {
  if (!task.value) return;

  task.value.reaction = reaction;

  TaskAPI.react({ params: { taskId: task.value.id, reaction } });
};
</script>

<template>
  <main class="flex flex-col flex-1">
    <div class="min-h-80vh flex-1 flex">
      <GameView :task="task">
        <Button
          as="nuxt-link"
          to="/game/settings"
          :label="t('start_game')"
          size="large"
          rounded
          class="mt-12 mb-8 text-xl px-6"
        />

        <GamePlayCard @react="react" :task="task" :categories="categories" with-tags class="my-auto" />
      </GameView>
    </div>
    <div class="px-2 py-8 md:pt-12 md:pb-16 md:px-6 xl:px-8 relative z-1 bg-surface-700">
      <h2 class="block text-2xl mt-0 md:text-5xl mb-12 text-center text-900">
        {{ t('similar_title') }}
      </h2>

      <div class="flex flex-col lg:flex-row gap-3 lg:gap-2 xl:gap-3">
        <NuxtLink v-for="item in similar" :key="item.id" :to="`/tasks/${item.id}`" class="flex-1 block">
          <GameSimpleCard
            :task="item"
            :categories="categories"
            with-tags
            :media-preview="false"
            class="hover:shadow-3 h-full transition"
          />
        </NuxtLink>
      </div>
    </div>
    <LayoutFooter />
  </main>
</template>

<i18n>
en:
  similar_title: Similar tasks
  start_game: Start game

ru:
  similar_title: Похожие задания
  start_game: Начать игру

de:
  similar_title: Ähnliche Aufgaben
  start_game: Spiel starten

es:
  similar_title: Tareas similares
  start_game: iniciar juego

fr:
  similar_title: Tâches similaires
  start_game: Commencer le jeu
</i18n>
